// This component is a simplified variant of the original `AddToCartButton` located at `apps/shop/src/components/AddToCartButton`.
// It was specifically created to add monitoring plans to the cart. This version is decoupled from dependencies and specific logic that are not necessary here.
//
// TODO: Evaluate the possibility of merging this component with the original `AddToCartButton` in a shared location to improve code reuse and maintenance.

import { LoadingSpinner } from '@ecomm/checkout-icons'
import { logError } from '@ecomm/error-handling'
import { localStorage } from '@simplisafe/ewok'
import { IOAddServicePlanToCart } from '@simplisafe/ss-ecomm-data/cart'
import type { AddServicePlanBody } from '@simplisafe/ss-ecomm-data/commercetools/cart'
import type { ImmutableCart } from '@simplisafe/ss-ecomm-data/commercetools/cart'
import * as O from 'fp-ts/lib/Option'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { trackProductAdded } from '@ecomm/cdp-tracking'
import { useLocale } from '@ecomm/data-hooks'
import { useNavigate } from '@ecomm/framework/useNavigate'
import { getCurrencyFromLocale } from '@ecomm/utils/locale/currencyCode'
import AddToCartError from './AddToCartError'

type AddPlanToCartButtonProps = {
  readonly buttonText: string
  readonly sku: string
  readonly quantity?: number
  readonly className?: string
  readonly isSecondary?: boolean
  readonly onClick?: () => void
}

type AddToCartErrorType = 'recoverable' | 'unrecoverable' | null

type AddPlanToCartButtonState = {
  readonly showSpinner: boolean
  readonly addToCartError: AddToCartErrorType
}

export function AddPlanToCartButtonProduct({
  buttonText,
  sku,
  quantity = 1,
  className = '',
  isSecondary = false,
  onClick = () => null
}: AddPlanToCartButtonProps) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const locale = useLocale()

  const [state, setState] = useState<AddPlanToCartButtonState>({
    showSpinner: true,
    addToCartError: null
  })

  const addToCart = useCallback(() => {
    const handleSuccess: Parameters<typeof IOAddServicePlanToCart>[2] =
      cart => {
        const lastAddedProductIndex =
          cart?.lineItems.findLastIndex(i => i.sku === sku) || -1
        const item = cart?.lineItems[lastAddedProductIndex]

        trackProductAdded({
          cartId: cart?.id || '',
          currency: getCurrencyFromLocale(locale),
          total: cart?.totalPrice || 0,
          products: [
            {
              name: item?.name[locale] ?? '',
              product_id: item?.sku ?? '',
              item_list_id: item?.lineItemId ?? '',
              price: item?.price,
              quantity: quantity
            }
          ]
        })
        onClick()
        setState({ ...state, showSpinner: false })
        navigate('/cart')
      }

    const handleFailure = () => {
      setState({ ...state, showSpinner: false, addToCartError: 'recoverable' })
    }

    O.fold(
      () => {
        setState({
          ...state,
          showSpinner: false,
          addToCartError: 'unrecoverable'
        })
        logError(Error('Cannot add to cart: received null/empty sku'))
      },
      (_sku: string) => {
        setState({ ...state, showSpinner: true })

        const parentId: string = localStorage.get('parentId') || ''

        const productWithParentId: AddServicePlanBody = {
          action: 'addServicePlan',
          packageParentId: parentId,
          sku: _sku
        }

        dispatch(
          IOAddServicePlanToCart(
            [productWithParentId],
            handleFailure,
            handleSuccess
          )
        )
      }
    )(O.fromNullable(sku))
  }, [quantity])

  useEffect(() => {
    sku && setState({ ...state, showSpinner: false })
  }, [sku])

  return (
    <>
      <button
        className={
          isSecondary
            ? `btn btn-outlined btn-outlined-tertiary !text-neutral-black mt-2 text-lg hover:underline md:mt-0 ${className}`
            : `btn btn-solid-primary self-center ${className}`
        }
        onClick={addToCart}
      >
        {state.showSpinner ? (
          <LoadingSpinner
            fillColor={isSecondary ? 'black' : 'white'}
            size={24}
          />
        ) : (
          buttonText
        )}
      </button>
      {state.addToCartError ? (
        <div className="m-auto ml-2 max-w-[200px]">
          <AddToCartError
            errorType={state?.addToCartError}
            textAlign="center"
          />
        </div>
      ) : null}
    </>
  )
}
